import Cookies from 'js-cookie';
import { fetchConReintentos } from '@/utils/fetchConReintentos';
import { urlDeLoginDesdeNavegador } from '@/lib/auth/destinoTrasLogin';

/**
 * Cliente único para hablar con la API de TherapyRecap.
 *
 * Antes cada pantalla armaba su propia llamada: leía la cookie `authToken`,
 * componía la cabecera `Authorization` y pegaba la URL base a mano. Eso son 59
 * archivos con `fetch(` directo y 43 leyendo el token por su cuenta, y convierte
 * cualquier cambio en la forma de autenticar en una excursión por todo el
 * código. Este módulo existe para que esa decisión se tome en UN solo lugar.
 *
 * Dos cambios pendientes dependen de esta costura, y por eso vale la pena:
 *
 *  - **Cookie httpOnly**: cuando el backend viva en `api.therapyrecap.com` y la
 *    cookie deje de ser legible desde JavaScript, no habrá token que leer. Solo
 *    hay que mover `MODO_CREDENCIALES` a `'cookie'`; ningún llamador se entera.
 *  - **Refresh tokens**: hace falta un punto único donde interceptar el 401,
 *    renovar y repetir la petición. Ese punto es `manejarNoAutorizado`.
 */

const URL_BASE = process.env.NEXT_PUBLIC_API_URL ?? '';

/**
 * Cómo se le demuestra la identidad al backend.
 *
 * `'bearer'` — el token viaja en la cabecera `Authorization`, leído de una
 * cookie accesible desde JavaScript. Es lo que hay hoy, y su problema es
 * conocido: cualquier XSS se lleva la sesión.
 *
 * `'cookie'` — el navegador manda solo una cookie httpOnly. Requiere que el
 * frontend y la API compartan dominio padre (`www` y `api` bajo
 * `therapyrecap.com`), porque si no la cookie es de terceros y Safari la
 * descarta sin avisar.
 *
 * Cambiar esta constante es el paso 1 completo del lado del frontend.
 */
const MODO_CREDENCIALES: 'bearer' | 'cookie' = 'cookie';

export class ErrorDeApi extends Error {
    readonly estado: number;
    readonly cuerpo: unknown;

    constructor(mensaje: string, estado: number, cuerpo: unknown) {
        super(mensaje);
        this.name = 'ErrorDeApi';
        this.estado = estado;
        this.cuerpo = cuerpo;
    }
}

export interface OpcionesDeSolicitud extends Omit<RequestInit, 'body'> {
    /** Cuerpo de la petición. Un objeto se serializa a JSON; un FormData se manda tal cual. */
    cuerpo?: unknown;
    /** Deja pasar el 401 sin cerrar la sesión. Para sondeos que toleran no estar autenticado. */
    toleraNoAutorizado?: boolean;
    /**
     * Intentos máximos del transporte (por defecto los de `fetchConReintentos`: reintenta red y 503). Un 1 desactiva el
     * reintento, para mutaciones cuyo 503 es un fallo determinista de una dependencia y no un arranque en frío.
     */
    intentos?: number;
}

/**
 * Lee el token de sesión.
 *
 * Devuelve `undefined` en modo cookie —no hay nada que leer— y también en el
 * servidor, donde no existe `document`.
 */
function leerToken(): string | undefined {
    if (MODO_CREDENCIALES === 'cookie') {
        return undefined;
    }

    return Cookies.get('authToken');
}

/**
 * Responde si tiene sentido intentar una llamada autenticada.
 *
 * Existe para que nadie más lea la cookie. El patrón que había repartido por
 * los servicios y las pantallas era:
 *
 *     const token = Cookies.get('authToken');
 *     if (!token) throw new Error('No hay sesión activa.');
 *
 * y ese guard es una trampa esperando a la cookie httpOnly: en cuanto el token
 * deje de ser legible desde JavaScript, `Cookies.get` devuelve `undefined` y la
 * comprobación rechazaría a usuarios perfectamente logueados, en todas las
 * pantallas a la vez.
 *
 * Por eso en modo cookie devuelve `true`: desde JavaScript no hay forma de
 * saberlo, y la respuesta correcta pasa a ser "probá y que conteste el
 * servidor". Un 401 lo resuelve `manejarNoAutorizado`, que es justo el sitio
 * donde debe vivir esa decisión.
 */
export function haySesionProbable(): boolean {
    if (MODO_CREDENCIALES === 'cookie') {
        return true;
    }

    return Boolean(leerToken());
}

/**
 * Olvida la sesión del lado del cliente.
 *
 * En modo cookie esto NO cierra la sesión: la httpOnly solo la puede borrar el
 * servidor, y para eso está `POST /logout`. Lo que sí hace, en los dos modos, es
 * dejar el navegador sin la cookie legible.
 */
export function olvidarTokenDeSesion(): void {
    Cookies.remove('authToken');
}

/**
 * Barre la cookie legible que quedó de la época del modo bearer.
 *
 * Al cambiar a modo cookie conviven dos cookies con el MISMO nombre: la vieja,
 * host-only sobre `www` y escrita por JavaScript, y la nueva, httpOnly con
 * `Domain=.therapyrecap.com`. El navegador manda las dos en la misma cabecera
 * —`Cookie: authToken=A; authToken=B`— y el backend se queda con la PRIMERA,
 * comprobado. O sea que una cookie muerta puede tapar a la viva.
 *
 * Dejar de escribir la vieja no basta: la que ya está en el navegador sobrevive
 * hasta que caduque, treinta días después. Hay que borrarla explícitamente.
 *
 * En modo bearer no toca nada, porque ahí esa cookie es la credencial de verdad
 * y borrarla desconectaría al usuario en cada arranque.
 */
export function barrerCredencialLegada(): void {
    if (MODO_CREDENCIALES !== 'cookie') {
        return;
    }

    Cookies.remove('authToken');
}

/**
 * Cierra la sesión del lado del cliente y manda al login.
 *
 * Concentra lo que antes hacía `fetchConReintentos`: la política de qué hacer
 * ante un 401 es del cliente de API, no del transporte con reintentos.
 *
 * Cuando entren los refresh tokens, esto deja de ser el primer recurso: se
 * intentará renovar y repetir la petición, y solo si la renovación falla se
 * llegará hasta acá.
 */
function manejarNoAutorizado(): void {
    if (typeof window === 'undefined') {
        return;
    }

    // Sin esta guarda, un 401 servido en la propia pantalla de login provocaría
    // una redirección a sí misma en bucle.
    if (window.location.pathname.includes('/login')) {
        return;
    }

    Cookies.remove('authToken');
    localStorage.removeItem('authUser');
    // Se lee la pantalla ANTES de navegar: tras iniciar sesión se vuelve al
    // enlace profundo desde el que caducó la sesión, no al listado de pacientes.
    window.location.href = urlDeLoginDesdeNavegador({ reason: 'session_expired' });
}

/**
 * Compone la URL absoluta de un endpoint de la API.
 *
 * Para los consumidores que **no** pueden usar `clienteApi` porque traen su
 * propio fetcher — hoy el transporte de streaming del SDK `ai` en
 * `usePatientChat` —. Sin esto la tentación es pasar una ruta relativa, que el
 * navegador resuelve contra el origen del frontend y acaba entrando por el
 * rewrite de `next.config.mjs`: un salto de más por Vercel, y encima ajeno a
 * `NEXT_PUBLIC_API_URL`, así que un cambio de dominio de API no lo movería.
 */
export function urlDeApi(ruta: string): string {
    return construirUrl(ruta);
}

/**
 * Cabeceras de credencial para esos mismos consumidores externos.
 *
 * En modo cookie devuelve un objeto vacío a propósito: no hay token que leer y
 * la credencial la adjunta el navegador. Quien use esto en modo cookie tiene
 * que pedir además `credentials: 'include'` en su propio fetcher.
 */
export function cabecerasDeCredencial(): Record<string, string> {
    const token = leerToken();

    return token ? { Authorization: `Bearer ${token}` } : {};
}

function construirUrl(ruta: string): string {
    if (ruta.startsWith('http://') || ruta.startsWith('https://')) {
        return ruta;
    }

    return `${URL_BASE}${ruta.startsWith('/') ? ruta : `/${ruta}`}`;
}

/**
 * Prepara cabeceras y cuerpo.
 *
 * Un `FormData` se devuelve intacto y *sin* `Content-Type`: el navegador tiene
 * que poner el suyo con el `boundary` que genera. Fijarlo a mano rompe las
 * subidas de audio y de documentos con un error que no dice nada útil.
 */
function prepararCarga(cuerpo: unknown): { cuerpo?: BodyInit; cabeceras: Record<string, string> } {
    if (cuerpo === undefined) {
        return { cabeceras: {} };
    }

    if (cuerpo instanceof FormData) {
        return { cuerpo, cabeceras: {} };
    }

    return {
        cuerpo: JSON.stringify(cuerpo),
        cabeceras: { 'Content-Type': 'application/json' },
    };
}

/**
 * Ejecuta una petición contra la API y devuelve la `Response` cruda.
 *
 * Usalo cuando necesites la respuesta sin interpretar: descargas, streaming del
 * chat, o cuando quieras mirar el estado antes de leer el cuerpo. Para el caso
 * habitual, `solicitarJson`.
 */
export async function solicitar(ruta: string, opciones: OpcionesDeSolicitud = {}): Promise<Response> {
    const { cuerpo, toleraNoAutorizado, headers, intentos, ...resto } = opciones;

    const token = leerToken();
    const carga = prepararCarga(cuerpo);

    const respuesta = await fetchConReintentos(
        construirUrl(ruta),
        {
            ...resto,
            // En modo cookie el navegador adjunta la sesión por su cuenta, pero solo
            // si se lo pedimos explícitamente en una petición entre subdominios.
            credentials: MODO_CREDENCIALES === 'cookie' ? 'include' : resto.credentials,
            body: carga.cuerpo,
            headers: {
                ...carga.cabeceras,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                ...(headers as Record<string, string> | undefined),
            },
        },
        intentos
    );

    if (respuesta.status === 401 && !toleraNoAutorizado) {
        manejarNoAutorizado();
    }

    return respuesta;
}

/**
 * Ejecuta una petición y devuelve el JSON ya interpretado.
 *
 * Ante una respuesta de error lanza `ErrorDeApi` con el estado y el cuerpo, para
 * que quien llama pueda mostrar el mensaje del backend en vez de un genérico.
 */
export async function solicitarJson<T = unknown>(
    ruta: string,
    opciones: OpcionesDeSolicitud = {}
): Promise<T> {
    const respuesta = await solicitar(ruta, opciones);

    // 204 y 205 no traen cuerpo: intentar interpretarlos como JSON explota.
    const sinContenido = respuesta.status === 204 || respuesta.status === 205;
    const datos = sinContenido ? null : await respuesta.json().catch(() => null);

    if (!respuesta.ok) {
        const mensaje =
            (datos as { message?: string; error?: string } | null)?.message ??
            (datos as { message?: string; error?: string } | null)?.error ??
            `La petición a ${ruta} falló con estado ${respuesta.status}.`;

        throw new ErrorDeApi(mensaje, respuesta.status, datos);
    }

    return datos as T;
}

export const clienteApi = {
    obtener: <T = unknown>(ruta: string, opciones?: OpcionesDeSolicitud) =>
        solicitarJson<T>(ruta, { ...opciones, method: 'GET' }),

    crear: <T = unknown>(ruta: string, cuerpo?: unknown, opciones?: OpcionesDeSolicitud) =>
        solicitarJson<T>(ruta, { ...opciones, method: 'POST', cuerpo }),

    actualizar: <T = unknown>(ruta: string, cuerpo?: unknown, opciones?: OpcionesDeSolicitud) =>
        solicitarJson<T>(ruta, { ...opciones, method: 'PUT', cuerpo }),

    modificar: <T = unknown>(ruta: string, cuerpo?: unknown, opciones?: OpcionesDeSolicitud) =>
        solicitarJson<T>(ruta, { ...opciones, method: 'PATCH', cuerpo }),

    eliminar: <T = unknown>(ruta: string, opciones?: OpcionesDeSolicitud) =>
        solicitarJson<T>(ruta, { ...opciones, method: 'DELETE' }),

    /** Escotilla de escape para respuestas que no son JSON: streaming, descargas. */
    crudo: solicitar,
};
